import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { API_BASE } from '../../lib/utils';
import { Bell, Mail, Video, MapPin, XCircle, Check } from 'lucide-react';

const Notifications = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_BASE}/notifications/${user.id}`)
      .then(res => setNotifications(res.data.notifications || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user.id]);

  const markAllRead = async () => {
    await axios.post(`${API_BASE}/notifications/mark-all-read/${user.id}`);
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  if (loading) return <div className="p-12 flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0071e3]" /></div>;

  const unread = notifications.filter(n => !n.read).length;

  return (
    <div className="p-4 sm:p-8 md:p-12">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl sm:text-4xl font-semibold text-[#1d1d1f]">Notifications</h1>
            <p className="text-base text-[#86868b]">{unread > 0 ? `${unread} unread` : 'All caught up'}</p>
          </div>
          {unread > 0 && (
            <Button onClick={markAllRead} variant="ghost" className="text-sm text-[#0071e3]" data-testid="mark-all-read">
              Mark all as read
            </Button>
          )}
        </div>

        {notifications.length === 0 ? (
          <div className="text-center py-16">
            <Bell className="w-12 h-12 text-[#d2d2d7] mx-auto mb-4" />
            <p className="text-lg text-[#86868b]">No notifications yet</p>
            <p className="text-sm text-[#86868b]">You'll be notified when employers respond to your applications.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {notifications.map(n => {
              const Icon = n.type === 'rejection' ? XCircle : n.interview_type === 'online' ? Video : n.interview_type === 'face-to-face' ? MapPin : Mail;
              const iconColor = n.type === 'rejection' ? 'text-red-500' : 'text-green-600';
              const bgColor = n.type === 'rejection' ? 'bg-red-50' : 'bg-green-50';

              return (
                <div key={n.id} className={`rounded-2xl border p-5 transition-all ${n.read ? 'border-[#d2d2d7] bg-white' : 'border-[#0071e3] bg-blue-50/30'}`}
                  data-testid={`notification-${n.id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${bgColor}`}>
                      <Icon className={`w-5 h-5 ${iconColor}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-sm font-semibold text-[#1d1d1f]">{n.title}</h3>
                        {!n.read && <span className="w-2 h-2 rounded-full bg-[#0071e3] flex-shrink-0" />}
                      </div>
                      <p className="text-sm text-[#424245] leading-relaxed">{n.message}</p>
                      {n.interview_date && (
                        <p className="text-sm text-[#0071e3] font-medium mt-2">
                          {n.interview_type === 'online' ? 'Online Interview' : 'In-Person Interview'}: {n.interview_date} at {n.interview_time}
                        </p>
                      )}
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-xs text-[#86868b]">{new Date(n.created_at).toLocaleDateString('en-IE', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                        {n.email_sent && <span className="text-xs text-[#86868b] flex items-center gap-1"><Mail className="w-3 h-3" /> Email sent</span>}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Notifications;
